import { faPaperPlane } from '@fortawesome/pro-regular-svg-icons/faPaperPlane';
import { faTimes } from '@fortawesome/pro-solid-svg-icons/faTimes';
import useLocalStorage from '@propertypal/shared/src/hooks/useLocalStorage';
import { AgentResult, AgentResults, Reason } from '@propertypal/shared/src/types/agents';
import { Filters } from '@propertypal/shared/src/utils/search/getNlpFilters';
import AgentCheckRow from '@propertypal/web-ui/src/agents/AgentCheckRow';
import { BrowseLinks } from '@propertypal/web-ui/src/agents/AgentCheckRow.style';
import Button from '@propertypal/web-ui/src/buttons/Button';
import FontAwesomeIcon from '@propertypal/web-ui/src/icons/FontAwesomeIcon';
import PageWrapper from '@propertypal/web-ui/src/layout/PageWrapper';
import WhiteModal from '@propertypal/web-ui/src/modal/WhiteModal';
import PageNumbers from '@propertypal/web-ui/src/search/PageNumbers';
import { Text, Heading, SubHeading } from '@propertypal/web-ui/src/typography';
import Link from 'next/link';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { useTheme } from 'styled-components';
import HomeSearchBar from '../search/HomeSearchBar';
import AgentDetails from './AgentDetails';
import AgentEmailForm from './AgentEmailForm';
import {
  AgentBox,
  Badge,
  BottomBar,
  Content,
  EmptyBox,
  FormWrapper,
  RemoveAgent,
  SearchWrapper,
  SelectedAgents,
  TitleRow,
} from './AgentsResults.style.pn';

interface Props {
  results: AgentResults;
  loading: boolean;
  onSearch: (params: string) => void;
  reasons: Reason[];
}

const STORAGE_KEY = 'selected-agents';

const AgentsResults: FunctionComponent<Props> = (props) => {
  const theme = useTheme();
  const [selectedAgents, setSelectedAgents] = useLocalStorage<AgentResult[]>(STORAGE_KEY, []);
  const [sent, setSent] = useState<boolean>(false);
  const [category, setCategory] = useState<string>('pt=residential&st=sale');
  const [showEmailOverlay, setShowEmailOverlay] = useState<boolean>(false);

  const toggleAgent = (agentResult: AgentResult) => {
    const index = selectedAgents.findIndex((a) => a.accountNumber === agentResult.accountNumber);

    if (index >= 0) {
      const copyList = [...selectedAgents];
      copyList.splice(index, 1);
      setSelectedAgents(copyList);
    } else if (selectedAgents.length < 5) {
      setSelectedAgents([...selectedAgents, agentResult]);
    }
  };

  const handleSent = () => {
    setSelectedAgents([]);
    setSent(true);
  };

  const handleSearch = (_: string, text: string, filters: Filters) => {
    const location = filters.lat && filters.lng ? `&lat=${filters.lat}&lng=${filters.lng}&radius=5` : '';
    props.onSearch(`${category}&q=${encodeURIComponent(text)}${location}`);
  };

  const handleDeselectAll = () => {
    setSelectedAgents([]);
  };

  useEffect(() => {
    if (selectedAgents.length > 0) {
      setSent(false);
    }

    if (selectedAgents.length === 0) {
      setShowEmailOverlay(false);
    }
  }, [selectedAgents]);

  useEffect(() => {
    if (props.results.params?.category && props.results.params.saleType) {
      setCategory(`pt=${props.results.params.category[0]}&st=${props.results.params.saleType[0]}`);
    }
  }, [props.results]);

  if (props.results.agentDetails) {
    return <AgentDetails agentDetails={props.results.agentDetails} reasons={props.reasons} />;
  }

  return (
    <PageWrapper containerStyle={{ margin: '30px auto' }}>
      <Content>
        <div>
          <Heading>{props.results.description.complete}</Heading>

          <SearchWrapper>
            <HomeSearchBar
              containerStyle={{ margin: '0', backgroundColor: theme.backgroundLightest, maxWidth: 'none' }}
              onSubmit={handleSearch}
              extraNlpParams="termTypes=ACCOUNT%2CACCOUNT_GROUP%2CTOWN%2CAREA&addressSuggestions=false"
              placeholder="Postcode, town, area or agent"
              loading={props.loading}
              selectCategory={category}
              setSelectedCategory={setCategory}
            />
          </SearchWrapper>

          <Badge>{props.results.totalResults} Found</Badge>

          <TitleRow>
            <SubHeading mt={15} mb={15}>
              Search and Compare Estate Agents in Northern Ireland
            </SubHeading>
          </TitleRow>

          {props.results.results.map((agent) => (
            <AgentCheckRow
              key={agent.accountNumber}
              agent={agent}
              onCheckClick={toggleAgent}
              checked={selectedAgents.findIndex((a) => a.accountNumber === agent.accountNumber) >= 0}
            />
          ))}
        </div>

        <div>
          <TitleRow style={{ marginTop: 34 }}>
            <SubHeading fontSize={20}>
              Contact Agents {selectedAgents.length > 0 ? <Badge>{selectedAgents.length}</Badge> : ''}
            </SubHeading>

            <button data-testid="deselect-all-agents" onClick={handleDeselectAll} type="button">
              Remove all
            </button>
          </TitleRow>

          {selectedAgents.length === 0 && <EmptyBox>Select at least 1 agent to send a message</EmptyBox>}

          {selectedAgents.length > 0 && (
            <SelectedAgents>
              {selectedAgents.map((agent) => (
                <AgentBox
                  key={agent.accountNumber}
                  data-testid="selected-agent-box"
                  style={{
                    backgroundImage: `url(${agent.account.logo?.standard})`,
                    backgroundColor: agent.account.logo?.standardBackgroundColour,
                  }}
                >
                  <RemoveAgent type="button" onClick={() => toggleAgent(agent)}>
                    <FontAwesomeIcon icon={faTimes} color={theme.white} />
                  </RemoveAgent>
                </AgentBox>
              ))}
            </SelectedAgents>
          )}

          {selectedAgents.length > 0 && <Text mt={10}>(Max 5)</Text>}

          {!sent && selectedAgents.length > 0 && (
            <FormWrapper>
              <AgentEmailForm
                agents={selectedAgents.map((agent) => agent.account)}
                reasons={props.reasons}
                onSubmit={handleSent}
                compact
                contactAll
                loggedInUser={props.results.loggedInUser}
              />
            </FormWrapper>
          )}

          {sent && (
            <FormWrapper>
              <FontAwesomeIcon
                icon={faPaperPlane}
                color={theme.textDark}
                size={40}
                style={{ display: 'block', margin: '20px auto' }}
              />

              <Text mt={20} mb={20} align="center">
                Your enquiry has been sent!
              </Text>
            </FormWrapper>
          )}

          <SubHeading fontSize={20} mt={30}>
            Browse by area
          </SubHeading>

          <BrowseLinks>
            <Link href="/estate-agents/northern-ireland">Northern Ireland</Link>
            <Link href="/estate-agents/belfast">Belfast</Link>
            <Link href="/estate-agents/east-belfast">East Belfast</Link>
            <Link href="/estate-agents/north-belfast">North Belfast</Link>
            <Link href="/estate-agents/south-belfast">South Belfast</Link>
            <Link href="/estate-agents/west-belfast">West Belfast</Link>
            <Link href="/estate-agents/county-antrim">County Antrim</Link>
            <Link href="/estate-agents/county-antrim">County Armagh</Link>
            <Link href="/estate-agents/county-down">County Down</Link>
            <Link href="/estate-agents/county-fermanagh">County Fermanagh</Link>
            <Link href="/estate-agents/county-londonderry">County Londonderry</Link>
            <Link href="/estate-agents/county-tyrone">County Tyrone</Link>
          </BrowseLinks>
        </div>
      </Content>

      <PageNumbers
        currentUrl={props.results.url}
        currentPage={props.results.page + 1}
        totalPages={props.results.totalPages}
      />

      <BottomBar>
        <Button color={theme.textDark} disabled={selectedAgents.length === 0} onClick={() => setShowEmailOverlay(true)}>
          Contact Agents {selectedAgents.length > 0 ? `(${selectedAgents.length}/5)` : ''}
        </Button>
      </BottomBar>

      <WhiteModal title="Contact Agents" show={showEmailOverlay} onClose={() => setShowEmailOverlay(false)}>
        <SelectedAgents>
          {selectedAgents.map((agent) => (
            <AgentBox
              key={agent.accountNumber}
              style={{
                backgroundImage: `url(${agent.account.logo?.standard})`,
                backgroundColor: agent.account.logo?.standardBackgroundColour,
              }}
            >
              <RemoveAgent type="button" onClick={() => toggleAgent(agent)}>
                <FontAwesomeIcon icon={faTimes} color={theme.white} />
              </RemoveAgent>
            </AgentBox>
          ))}
        </SelectedAgents>

        {!sent && selectedAgents.length > 0 && <Text mt={10}>(Max 5)</Text>}

        {!sent && selectedAgents.length > 0 && (
          <AgentEmailForm
            agents={selectedAgents.map((agent) => agent.account)}
            reasons={props.reasons}
            onSubmit={handleSent}
            compact
            contactAll
            loggedInUser={props.results.loggedInUser}
          />
        )}

        {sent && (
          <>
            <FontAwesomeIcon
              icon={faPaperPlane}
              color={theme.textDark}
              size={40}
              style={{ display: 'block', margin: '20px auto' }}
            />

            <Text mt={20} mb={20} align="center">
              Your enquiry has been sent!
            </Text>
          </>
        )}
      </WhiteModal>
    </PageWrapper>
  );
};

export default AgentsResults;
